import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { post } from '@/lib/api';
import { cn, initials } from '@/lib/utils';

const LINK_FRESH_MS = 10 * 60_000;
const LINK_KEPT_MS = 14 * 60_000;

export function usePhotoLink(key: string | null | undefined): string | null {
  const query = useQuery({
    queryKey: ['file-link', key],
    queryFn: () => post<{ url: string }>('/files/presign-download', { key }),
    enabled: Boolean(key),
    staleTime: LINK_FRESH_MS,
    gcTime: LINK_KEPT_MS,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  return key ? (query.data?.url ?? null) : null;
}

export function PatientAvatar({
  name,
  photoKey,
  src,
  className,
  fallback,
}: {
  name: string;
  photoKey?: string | null;
  src?: string | null;
  className?: string;
  fallback?: ReactNode;
}) {
  const link = usePhotoLink(src ? null : photoKey);
  const shown = src ?? link;
  const [broken, setBroken] = useState<string | null>(null);
  const showImage = Boolean(shown) && broken !== shown;
  return (
    <span className={cn('relative overflow-hidden', className)} aria-hidden>
      {showImage ? (
        <img src={shown as string} alt="" className="absolute inset-0 h-full w-full object-cover" onError={() => setBroken(shown)} />
      ) : (
        (fallback ?? initials(name))
      )}
    </span>
  );
}
